import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteIcon } from '../icons';
import { useNotification } from '../shared/NotificationSystem';
import Loader from '../shared/Loader';
import styles from './Notifications.module.scss';
import { useNotifications } from '../../redux/hooks';
import { apiService } from '../../api/api.service';
import { INotification } from '../../types';

const IOS_STORE_URL = 'https://apps.apple.com/ru/app/rifelli/id6746193094';
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.urdev.trainingapp&pcampaignid=web_share';

const APP_VERSIONS_STORAGE_KEY = 'notifications_app_versions';

type AppVersionsStored = {
    iosLatestVersion: string;
    iosMinVersion: string;
    androidLatestVersion: string;
    androidMinVersion: string;
};

const getDefaultStored = (): AppVersionsStored => ({
    iosLatestVersion: '',
    iosMinVersion: '',
    androidLatestVersion: '',
    androidMinVersion: '',
});

const loadAppVersionsFromStorage = (): AppVersionsStored => {
    try {
        const raw = localStorage.getItem(APP_VERSIONS_STORAGE_KEY);
        if (!raw) return getDefaultStored();
        const data = JSON.parse(raw);
        return {
            iosLatestVersion: data.iosLatestVersion ?? '',
            iosMinVersion: data.iosMinVersion ?? '',
            androidLatestVersion: data.androidLatestVersion ?? '',
            androidMinVersion: data.androidMinVersion ?? '',
        };
    } catch {
        return getDefaultStored();
    }
};

const saveAppVersionsToStorage = (data: AppVersionsStored) => {
    try {
        localStorage.setItem(APP_VERSIONS_STORAGE_KEY, JSON.stringify(data));
    } catch {
        // ignore
    }
};

const Notifications: React.FC = () => {
    const { t, i18n } = useTranslation();
    const notification = useNotification();
    const {
        notifications,
        loading: isLoading,
        submitting: isSubmitting,
        error,
        getNotifications,
        createNotification,
        removeNotification
    } = useNotifications();

    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [selectedScreen, setSelectedScreen] = useState<string>('');
    const [externalLink, setExternalLink] = useState<string>('');
    const [isScheduled, setIsScheduled] = useState(false);
    const [scheduledDate, setScheduledDate] = useState<string>('');
    const [considerRecipientTime, setConsiderRecipientTime] = useState(false);
    const [activeSubscription, setActiveSubscription] = useState(true);
    const [inactiveSubscription, setInactiveSubscription] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [, setIsTablet] = useState(false);
    const [isCleanupPerformed, setIsCleanupPerformed] = useState(false);
    const [sortBy, setSortBy] = useState<'date' | 'status' | 'type' | 'subscription'>('date');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const [iosLatestVersion, setIosLatestVersion] = useState('');
    const [iosMinVersion, setIosMinVersion] = useState('');
    const [androidLatestVersion, setAndroidLatestVersion] = useState('');
    const [androidMinVersion, setAndroidMinVersion] = useState('');
    const [submittingIos, setSubmittingIos] = useState(false);
    const [submittingAndroid, setSubmittingAndroid] = useState(false);

    useEffect(() => {
        const stored = loadAppVersionsFromStorage();
        setIosLatestVersion(stored.iosLatestVersion);
        setIosMinVersion(stored.iosMinVersion);
        setAndroidLatestVersion(stored.androidLatestVersion);
        setAndroidMinVersion(stored.androidMinVersion);
    }, []);

    useEffect(() => {
        const checkDeviceSize = () => {
            setIsMobile(window.innerWidth < 600);
            setIsTablet(window.innerWidth >= 600 && window.innerWidth < 960);
        };

        checkDeviceSize();
        window.addEventListener('resize', checkDeviceSize);

        return () => {
            window.removeEventListener('resize', checkDeviceSize);
        };
    }, []);

    useEffect(() => {
        getNotifications();
    }, [getNotifications]);

    useEffect(() => {
        if (error) {
            // Проверяем, является ли ошибка ключом перевода
            const errorMessage = error.startsWith('notifications.errors.') || error.startsWith('common.')
                ? t(error)
                : error;
            notification.showError(errorMessage);
        }
    }, [error, notification, t]);

    const handleSubmit = async () => {
        try {
            // Безопасное получение языка с fallback
            const currentLanguage = i18n.language || 'ru';
            const languageCode = currentLanguage.toUpperCase();

            // Конвертируем локальное время в ISO формат с локальным timezone
            let scheduledAtISO: string | undefined = undefined;
            if (isScheduled && scheduledDate) {
                const localDate = new Date(scheduledDate);
                // Получаем смещение в минутах и конвертируем в формат ±HH:MM
                const offset = -localDate.getTimezoneOffset();
                const offsetHours = Math.floor(Math.abs(offset) / 60).toString().padStart(2, '0');
                const offsetMinutes = (Math.abs(offset) % 60).toString().padStart(2, '0');
                const offsetSign = offset >= 0 ? '+' : '-';

                // Форматируем дату в ISO с локальным timezone
                const year = localDate.getFullYear();
                const month = (localDate.getMonth() + 1).toString().padStart(2, '0');
                const day = localDate.getDate().toString().padStart(2, '0');
                const hours = localDate.getHours().toString().padStart(2, '0');
                const minutes = localDate.getMinutes().toString().padStart(2, '0');
                const seconds = '00';

                scheduledAtISO = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
            }

            const payload: any = {
                title,
                body,
                type: isScheduled ? 'scheduled' : 'immediate',
                language: languageCode,
                scheduled_at: scheduledAtISO,
                consider_timezone: considerRecipientTime,
                active_subscriptions: activeSubscription,
                inactive_subscriptions: inactiveSubscription,
                is_active: true,
            };

            // Добавляем data, если выбран экран или указана внешняя ссылка
            if (selectedScreen || externalLink.trim()) {
                // Автоматически добавляем https:// если протокол не указан
                let processedExternalLink = externalLink.trim();
                if (processedExternalLink && !processedExternalLink.match(/^https?:\/\//)) {
                    processedExternalLink = `https://${processedExternalLink}`;
                }

                payload.data = {
                    ...(selectedScreen && { screen: selectedScreen, tab: selectedScreen }),
                    ...(processedExternalLink && { external_link: processedExternalLink }),
                    language: languageCode
                };
            } else {
                // Если не выбран экран и нет внешней ссылки, добавляем пустой data объект
                // чтобы мобильное приложение могло корректно обработать уведомление
                payload.data = {
                    language: languageCode
                };
            }

            await createNotification(payload);
            notification.showSuccess(t('notifications.success.sent'));
            resetForm();
            // Обновляем список уведомлений после отправки
            await getNotifications();
        } catch (err) {
            // Ошибка уже обрабатывается в thunk и отображается через useEffect выше
        }
    };

    const resetForm = () => {
        setTitle('');
        setBody('');
        setSelectedScreen('');
        setExternalLink('');
        setIsScheduled(false);
        setScheduledDate('');
        setConsiderRecipientTime(false);
        setActiveSubscription(true);
        setInactiveSubscription(false);
    };

    const handleSubmitAppVersion = async (platform: 'ios' | 'android') => {
        const isIos = platform === 'ios';
        const latestVersion = isIos ? iosLatestVersion : androidLatestVersion;
        const minimumVersion = isIos ? iosMinVersion : androidMinVersion;
        const storeUrl = isIos ? IOS_STORE_URL : ANDROID_STORE_URL;

        if (!latestVersion.trim() || !minimumVersion.trim()) {
            notification.showError(t('notifications.appVersions.errors.fillAll'));
            return;
        }

        if (isIos) {
            setSubmittingIos(true);
        } else {
            setSubmittingAndroid(true);
        }

        try {
            await apiService.updateAppVersion(platform, {
                minimum_version: minimumVersion.trim(),
                latest_version: latestVersion.trim(),
                store_url: storeUrl,
                release_notes: '',
            });
            notification.showSuccess(t('notifications.appVersions.success'));
            const latestTrimmed = latestVersion.trim();
            const minimumTrimmed = minimumVersion.trim();
            const stored = loadAppVersionsFromStorage();
            if (isIos) {
                saveAppVersionsToStorage({
                    ...stored,
                    iosLatestVersion: latestTrimmed,
                    iosMinVersion: minimumTrimmed,
                });
            } else {
                saveAppVersionsToStorage({
                    ...stored,
                    androidLatestVersion: latestTrimmed,
                    androidMinVersion: minimumTrimmed,
                });
            }
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || t('notifications.appVersions.errors.sendFailed');
            notification.showError(message);
        } finally {
            if (isIos) {
                setSubmittingIos(false);
            } else {
                setSubmittingAndroid(false);
            }
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await removeNotification(id);
            notification.showSuccess(t('notifications.success.deleted'));
        } catch (err) {
            // Ошибка уже обрабатывается в thunk и отображается через useEffect выше
        }
    };

    const getDefaultDateTime = () => {
        const now = new Date();
        now.setMinutes(now.getMinutes() + 30);
        return now.toISOString().slice(0, 16);
    };

    // Функция для форматирования даты с конвертацией timezone (для created_at)
    // Конвертирует UTC → локальное время пользователя
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    };

    // Функция для форматирования абсолютного времени без конвертации (для scheduled_at)
    // Показывает время "как есть", игнорируя timezone
    const formatAbsoluteDate = (dateString: string) => {
        // Парсим дату вручную из ISO строки, чтобы получить "абсолютное" время
        // Формат: "YYYY-MM-DDTHH:MM:SS" или "YYYY-MM-DDTHH:MM:SS±HH:MM"
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);

        if (match) {
            const [, year, month, day, hours, minutes] = match;
            return `${day}.${month}.${year} ${hours}:${minutes}`;
        }

        // Fallback на стандартное форматирование
        return formatDate(dateString);
    };

    // Функция сортировки уведомлений
    const sortNotifications = (notifications: INotification[]) => {
        const sorted = [...notifications].sort((a, b) => {
            let result = 0;

            switch (sortBy) {
                case 'date':
                    result = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                    break;
                case 'status':
                    if (a.is_sent !== b.is_sent) {
                        result = a.is_sent ? 1 : -1;
                    } else if (a.is_active !== b.is_active) {
                        result = a.is_active ? -1 : 1;
                    } else {
                        result = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                    }
                    break;
                case 'type':
                    if (a.type !== b.type) {
                        result = a.type === 'scheduled' ? -1 : 1;
                    } else {
                        result = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                    }
                    break;
                case 'subscription':
                    // Сортировка по типу подписки (активные/неактивные/смешанные)
                    const getSubscriptionPriority = (notification: INotification) => {
                        if (notification.active_subscriptions && !notification.inactive_subscriptions) return 1; // Только активные
                        if (!notification.active_subscriptions && notification.inactive_subscriptions) return 2; // Только неактивные  
                        if (notification.active_subscriptions && notification.inactive_subscriptions) return 0; // Смешанные (высший приоритет)
                        return 3; // Не определено
                    };

                    const aPriority = getSubscriptionPriority(a);
                    const bPriority = getSubscriptionPriority(b);

                    if (aPriority !== bPriority) {
                        result = aPriority - bPriority;
                    } else {
                        result = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                    }
                    break;
            }

            return sortOrder === 'desc' ? -result : result;
        });

        return sorted;
    };



    // Проверяет, старше ли уведомление 14 дней
    const isNotificationOlderThan14Days = (notification: INotification): boolean => {
        const notificationDate = new Date(notification.created_at);
        const now = new Date();
        const diffInMs = now.getTime() - notificationDate.getTime();
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
        return diffInDays > 14;
    };

    // Автоматически удаляет уведомления старше 14 дней
    const cleanupOldNotifications = useCallback(async (notificationsList: INotification[]) => {
        if (isCleanupPerformed) return; // Предотвращаем повторную очистку

        const oldNotifications = notificationsList.filter(isNotificationOlderThan14Days);

        if (oldNotifications.length > 0) {
            setIsCleanupPerformed(true); // Отмечаем, что очистка начата

            try {
                // Удаляем старые уведомления последовательно для предотвращения переполнения пула
                for (const notif of oldNotifications) {
                    await apiService.deleteNotification(notif.id);
                }

                // Обновляем список уведомлений после удаления
                await getNotifications();

                // Показываем уведомление об очистке только если есть что удалить
                notification.showSuccess(
                    t('notifications.cleanup.success', {
                        count: oldNotifications.length,
                        defaultValue: `Удалено ${oldNotifications.length} старых уведомлений`
                    })
                );
            } catch (error) {
                console.error('Ошибка при очистке старых уведомлений:', error);
                notification.showError(
                    t('notifications.cleanup.error', {
                        defaultValue: 'Ошибка при очистке старых уведомлений'
                    })
                );
                setIsCleanupPerformed(false); // Сбрасываем флаг при ошибке
            }
        }
    }, [getNotifications, notification, t, isCleanupPerformed]);

    // Фильтрует уведомления по языку, исключая те, что старше 14 дней, и применяет сортировку
    const filteredNotifications = sortNotifications(
        notifications.filter(notif => {
            // Фильтруем по текущему языку
            const currentLanguage = (i18n.language || 'ru').toUpperCase();
            const notificationLanguage = notif.language?.toUpperCase() || 'RU';

            return notificationLanguage === currentLanguage && !isNotificationOlderThan14Days(notif);
        })
    );

    // Автоматическая очистка старых уведомлений после их загрузки (выполняется один раз)
    useEffect(() => {
        if (notifications.length > 0 && !isLoading && !isCleanupPerformed) {
            cleanupOldNotifications(notifications);
        }
        // Убираем cleanupOldNotifications из зависимостей для предотвращения лишних вызовов
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications.length, isLoading, isCleanupPerformed]);

    return (
        <div className={styles.container}>
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    <div className={styles.panel}>
                        <h2 className={styles.panelTitle}>{t('notifications.title')}</h2>
                        <div className={styles.form}>
                            <div className={styles.formGroup}>
                                <input
                                    type="text"
                                    className={styles.inputField}
                                    placeholder={t('notifications.notificationTitle')}
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    required
                                />
                                <textarea
                                    className={styles.textarea}
                                    placeholder={t('notifications.notificationText')}
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    rows={isMobile ? 2 : 3}
                                    required
                                />
                                <div className={styles.navigationContainer}>
                                    <div className={styles.externalLinkSection}>
                                        <input
                                            type="url"
                                            className={styles.inputField}
                                            placeholder={t('notifications.externalLinkPlaceholder') || 'Внешняя ссылка'}
                                            value={externalLink}
                                            onChange={(e) => setExternalLink(e.target.value)}
                                        />
                                    </div>

                                    <div className={styles.appPageSection}>
                                        <label className={styles.label}>
                                            {t('notifications.appPage') || 'Страница приложения (необязательно)'}
                                        </label>
                                        <div className={styles.quickLinkButtons}>
                                            <button
                                                type="button"
                                                className={`${styles.quickLinkButton} ${selectedScreen === 'Live' ? styles.active : ''}`}
                                                onClick={() => setSelectedScreen('Live')}
                                                title={t('notifications.quickLinks.live') || 'Ссылка на трансляцию'}
                                            >
                                                📺 {t('notifications.quickLinks.live') || 'Live'}
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.quickLinkButton} ${selectedScreen === 'Training' ? styles.active : ''}`}
                                                onClick={() => setSelectedScreen('Training')}
                                                title={t('notifications.quickLinks.training') || 'Ссылка на тренировки'}
                                            >
                                                🏋️ {t('notifications.quickLinks.training') || 'Training'}
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.quickLinkButton} ${selectedScreen === 'Bonus' ? styles.active : ''}`}
                                                onClick={() => setSelectedScreen('Bonus')}
                                                title={t('notifications.quickLinks.bonus') || 'Ссылка на бонусы'}
                                            >
                                                🎁 {t('notifications.quickLinks.bonus') || 'Bonus'}
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.quickLinkButton} ${selectedScreen === 'Instruction' ? styles.active : ''}`}
                                                onClick={() => setSelectedScreen('Instruction')}
                                                title={t('notifications.quickLinks.instruction') || 'Ссылка на инструкции'}
                                            >
                                                📖 {t('notifications.quickLinks.instruction') || 'Instruction'}
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.quickLinkButton} ${selectedScreen === 'Profile' ? styles.active : ''}`}
                                                onClick={() => setSelectedScreen('Profile')}
                                                title={t('notifications.quickLinks.profile') || 'Ссылка на профиль'}
                                            >
                                                👤 {t('notifications.quickLinks.profile') || 'Profile'}
                                            </button>
                                        </div>

                                    </div>


                                </div>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>
                                        <input
                                            type="checkbox"
                                            className={styles.toggle}
                                            checked={isScheduled}
                                            onChange={(e) => {
                                                setIsScheduled(e.target.checked);
                                                if (e.target.checked) {
                                                    setScheduledDate(getDefaultDateTime());
                                                }
                                            }}
                                        />
                                        {t('notifications.scheduleDelivery')}
                                    </label>
                                    {isScheduled && (
                                        <>
                                            <input
                                                type="datetime-local"
                                                className={styles.inputField}
                                                value={scheduledDate}
                                                onChange={(e) => setScheduledDate(e.target.value)}
                                                min={new Date().toISOString().slice(0, 16)}
                                                aria-label={t('notifications.scheduledDateTime')}
                                            />
                                            <label className={styles.label}>
                                                <input
                                                    type="checkbox"
                                                    className={styles.toggle}
                                                    checked={considerRecipientTime}
                                                    onChange={(e) => setConsiderRecipientTime(e.target.checked)}
                                                />
                                                {t('notifications.considerRecipientTime')}
                                            </label>
                                        </>
                                    )}
                                </div>
                                <div className={styles.formGroup}>
                                    {/* This div is kept empty to maintain the grid layout */}
                                </div>
                            </div>
                            <div className={styles.subscriptionFilters}>
                                <h3 className={styles.filterTitle}>{t('notifications.targetAudience')}</h3>

                                <div className={styles.filterOptions}>
                                    <label className={styles.label}>
                                        <input
                                            type="checkbox"
                                            className={styles.toggle}
                                            checked={activeSubscription}
                                            onChange={(e) => setActiveSubscription(e.target.checked)}
                                        />
                                        {t('notifications.activeSubscription')}
                                    </label>
                                    <label className={styles.label}>
                                        <input
                                            type="checkbox"
                                            className={styles.toggle}
                                            checked={inactiveSubscription}
                                            onChange={(e) => setInactiveSubscription(e.target.checked)}
                                        />
                                        {t('notifications.inactiveSubscription')}
                                    </label>
                                </div>
                            </div>
                            <button
                                className={styles.button}
                                onClick={handleSubmit}
                                disabled={
                                    !title ||
                                    !body ||
                                    (isScheduled && !scheduledDate) ||
                                    (!activeSubscription && !inactiveSubscription) ||
                                    isSubmitting
                                }
                            >
                                {isSubmitting ? t('notifications.sending') : t('notifications.send')}
                            </button>
                        </div>
                    </div>

                    <div className={styles.versionBlocksRow}>
                        <div className={styles.panel}>
                            <h2 className={styles.panelTitle}>{t('notifications.appVersions.platformIos')}</h2>
                            <div className={styles.form}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('notifications.appVersions.newVersionLabel')}</label>
                                    <input
                                        type="text"
                                        className={styles.inputField}
                                        placeholder={t('notifications.appVersions.currentVersion')}
                                        value={iosLatestVersion}
                                        onChange={(e) => setIosLatestVersion(e.target.value)}
                                    />
                                    <label className={styles.label}>{t('notifications.appVersions.minimumVersion')}</label>
                                    <input
                                        type="text"
                                        className={styles.inputField}
                                        placeholder={t('notifications.appVersions.minimumVersion')}
                                        value={iosMinVersion}
                                        onChange={(e) => setIosMinVersion(e.target.value)}
                                    />
                                    <a href={IOS_STORE_URL} target="_blank" rel="noopener noreferrer" className={styles.storeLink}>{t('notifications.appVersions.storeLinkLabel')}</a>
                                    <button
                                        className={styles.button}
                                        onClick={() => handleSubmitAppVersion('ios')}
                                        disabled={submittingIos}
                                    >
                                        {submittingIos ? t('notifications.appVersions.sending') : t('notifications.appVersions.submit')}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className={styles.panel}>
                            <h2 className={styles.panelTitle}>{t('notifications.appVersions.platformAndroid')}</h2>
                            <div className={styles.form}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('notifications.appVersions.newVersionLabel')}</label>
                                    <input
                                        type="text"
                                        className={styles.inputField}
                                        placeholder={t('notifications.appVersions.currentVersion')}
                                        value={androidLatestVersion}
                                        onChange={(e) => setAndroidLatestVersion(e.target.value)}
                                    />
                                    <label className={styles.label}>{t('notifications.appVersions.minimumVersion')}</label>
                                    <input
                                        type="text"
                                        className={styles.inputField}
                                        placeholder={t('notifications.appVersions.minimumVersion')}
                                        value={androidMinVersion}
                                        onChange={(e) => setAndroidMinVersion(e.target.value)}
                                    />
                                    <a href={ANDROID_STORE_URL} target="_blank" rel="noopener noreferrer" className={styles.storeLink}>{t('notifications.appVersions.storeLinkLabel')}</a>
                                    <button
                                        className={styles.button}
                                        onClick={() => handleSubmitAppVersion('android')}
                                        disabled={submittingAndroid}
                                    >
                                        {submittingAndroid ? t('notifications.appVersions.sending') : t('notifications.appVersions.submit')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <h2 className={styles.historyTitle}>{t('notifications.historyTitle')}</h2>

                    {/* Панель сортировки */}
                    <div className={styles.sortPanel}>
                        <div className={styles.sortGroup}>
                            <label className={styles.sortLabel}>
                                {t('notifications.sortBy') || 'Сортировать по:'}
                            </label>
                            <select
                                className={styles.sortSelect}
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as 'date' | 'status' | 'type' | 'subscription')}
                            >
                                <option value="date">{t('notifications.sortByDate') || 'Дате создания'}</option>
                                <option value="status">{t('notifications.sortByStatus') || 'Статусу'}</option>
                                <option value="type">{t('notifications.sortByType') || 'Типу доставки'}</option>
                                <option value="subscription">{t('notifications.sortBySubscription') || 'Типу подписки'}</option>
                            </select>
                        </div>
                        <div className={styles.sortGroup}>
                            <label className={styles.sortLabel}>
                                {t('notifications.sortOrder') || 'Порядок:'}
                            </label>
                            <select
                                className={styles.sortSelect}
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                            >
                                <option value="desc">{t('notifications.sortDesc') || 'По убыванию'}</option>
                                <option value="asc">{t('notifications.sortAsc') || 'По возрастанию'}</option>
                            </select>
                        </div>
                    </div>

                    <div className={styles.grid}>
                        {filteredNotifications.length === 0 ? (
                            <div className={`${styles.card} ${isMobile ? '' : styles.emptyState}`} style={{ gridColumn: '1 / -1' }}>
                                <div className={styles.cardContent} style={{ textAlign: 'center' }}>
                                    <p>{t('notifications.noNotifications')}</p>
                                </div>
                            </div>
                        ) : (
                            filteredNotifications.map((notification) => (
                                <div className={styles.card} key={notification.id}>
                                    <div className={styles.cardContent}>
                                        <div className={styles.cardHeader}>
                                            <h3 className={styles.cardTitle}>{notification.title}</h3>
                                            <button
                                                className={styles.iconButton}
                                                onClick={() => handleDelete(notification.id)}
                                                title={t('common.delete')}
                                                aria-label={t('common.delete')}
                                            >
                                                <DeleteIcon />
                                            </button>
                                        </div>
                                        <p className={styles.cardText}>{notification.body}</p>
                                        {notification.data && (
                                            <div className={styles.navigationInfo}>
                                                {notification.data.screen && (
                                                    <span className={styles.navigationText}>
                                                        📱 {t('notifications.quickLinks.' + notification.data.screen.toLowerCase()) || notification.data.screen}
                                                    </span>
                                                )}
                                                {notification.data.external_link && (
                                                    <span className={styles.externalLinkText}>
                                                        🔗 {notification.data.external_link}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className={styles.status}>
                                            <span className={`${styles.statusChip} ${notification.is_sent ? styles.success : notification.is_active ? styles.primary : styles.inactive}`}>
                                                {notification.is_sent
                                                    ? t('notifications.sent')
                                                    : notification.type === 'scheduled'
                                                        ? t('notifications.scheduled')
                                                        : t('notifications.immediate')
                                                }
                                            </span>
                                            {!notification.is_sent && notification.scheduled_at && (
                                                <div className={styles.scheduleInfo}>
                                                    {t('notifications.scheduledTime')}: {formatAbsoluteDate(notification.scheduled_at)}
                                                </div>
                                            )}
                                            <div className={styles.createdAt}>
                                                {t('notifications.createdAt')}: {formatDate(notification.created_at)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Notifications;